import { queryOptions } from '@tanstack/react-query'
import { subscriptionService, OrderItemsParams } from '@/services/subscriptionService'
import { queryClient, queryKeys } from '@/services/queryClient'
import { paymentService } from '@/services/paymentService'
import { faqService, Faq } from '@/services/faqService'
import { BillingCycleType, Moment, MOMENT_SLUGS, SectionKey } from '@/constants'
import { useSessionStore } from '@/stores'
import type { PortalSection, PortalSettings } from '@/types'
import { campaignProductIdOf } from './crossSell'
import { itemIdFromPath, pageFromPath } from './portalNav'
import { getRenderedSections, resolveMoment } from './sectionsConfig'
import { expectedPageMoment, stepSnapshotForPath } from './stepSnapshots'

export const PORTAL_CACHE = { staleTime: 30_000, gcTime: 10 * 60_000 }

export const LATEST_CYCLE_PARAMS: OrderItemsParams = {
  billing_cycle_type: BillingCycleType.Recurring,
  latest_cycle: true,
}

export function orderGroupParams(subscriptionId?: number | string): OrderItemsParams {
  return { is_active: false, checkout: false, ...(subscriptionId ? { subscription_id: subscriptionId } : {}) }
}

export function orderItemsQuery(params: OrderItemsParams) {
  return queryOptions({
    queryKey: queryKeys.portalOrderItems(params),
    queryFn: () => subscriptionService.getOrderItems(params),
    ...PORTAL_CACHE,
  })
}

export function portalItemQuery(itemId: string | number) {
  return queryOptions({
    queryKey: queryKeys.portalItem(itemId),
    queryFn: () => subscriptionService.getItem(itemId),
    ...PORTAL_CACHE,
  })
}

export function paymentSourcesQuery() {
  return queryOptions({
    queryKey: queryKeys.portalPaymentSources(),
    queryFn: () => paymentService.getPaymentSources(),
    ...PORTAL_CACHE,
  })
}

export const PAYMENT_ISSUE_PARAMS: OrderItemsParams = { billing_cycle_type: BillingCycleType.Recurring }

export function crossSellQuery(campaignProductId: number | undefined) {
  return queryOptions({
    queryKey: [...queryKeys.portal, 'cross-sell', campaignProductId],
    queryFn: () => subscriptionService.getPortalRelatedProducts(campaignProductId!),
    ...PORTAL_CACHE,
  })
}

export function faqsQuery(faqIds: number[]) {
  return queryOptions({
    queryKey: [...queryKeys.portal, 'faqs', faqIds.slice().sort().join(',')],
    queryFn: async () => {
      const res: any = await faqService.getFaqsByIds(faqIds)
      return (Array.isArray(res) ? res : (res?.results ?? [])) as Faq[]
    },
    staleTime: 10 * 60_000,
  })
}

function visibleSections(sections: PortalSection[]): PortalSection[] {
  return sections
    .filter(section => section && section.is_visible !== false)
    .flatMap(section =>
      section.key === SectionKey.Columns
        ? [
            section,
            ...visibleSections(
              ((section.data as any)?.columns ?? []).filter((c: PortalSection) => c?.key !== SectionKey.Columns)
            ),
          ]
        : [section]
    )
}

export function prefetchPortalRoute(path: string) {
  const { pathname, searchParams } = new URL(path, window.location.origin)
  const session = stepSnapshotForPath(pathname) ?? useSessionStore.getState().session
  const page = pageFromPath(pathname)
  const moment = expectedPageMoment(page, pathname) ?? resolveMoment(session)
  const itemId = itemIdFromPath(pathname)

  if (itemId !== undefined) {
    void queryClient.prefetchQuery(portalItemQuery(String(itemId)))
  }
  if (pathname.startsWith('/portal/account/payment')) {
    void queryClient.prefetchQuery(paymentSourcesQuery())
  }

  const settings = (useSessionStore.getState().campaign as any)?.portal_settings as PortalSettings | undefined
  const sections = pathname.startsWith('/portal/account') ? [] : getRenderedSections(settings, page, moment)
  visibleSections(sections).forEach(section => {
    switch (section.key) {
      case SectionKey.SubscriptionList:
        void queryClient.prefetchQuery(orderItemsQuery(LATEST_CYCLE_PARAMS))
        break
      case SectionKey.OrderList:
        void queryClient.prefetchQuery(orderItemsQuery(orderGroupParams(searchParams.get('subscription') || undefined)))
        break
      case SectionKey.ShippingAndPayment:
        void queryClient.prefetchQuery(paymentSourcesQuery())
        break
      case SectionKey.FaqSection: {
        const faqIds = (section.data as any)?.faq_ids
        if (Array.isArray(faqIds) && faqIds.length) {
          void queryClient.prefetchQuery(faqsQuery(faqIds))
        }
        break
      }
      case SectionKey.MomentCard:
        if (moment === MOMENT_SLUGS[Moment.PaymentIssue]) {
          void queryClient.prefetchQuery(orderItemsQuery(PAYMENT_ISSUE_PARAMS))
        }
        break
      case SectionKey.CrossSellCarousel: {
        const campaignProductId = itemId === undefined ? campaignProductIdOf(session?.moment_item) : undefined
        if (campaignProductId != null) {
          void queryClient.prefetchQuery(crossSellQuery(campaignProductId))
        }
        break
      }
    }
  })
}
